<script setup lang="ts">
/**
 * Card "Biometria" em /user/security.
 *
 * Sempre visivel. O toggle so eh interativo quando: ambiente nativo +
 * hardware biometrico no aparelho + biometria cadastrada no SO. Em
 * todos os outros casos o toggle aparece desabilitado e em OFF, com
 * uma mensagem explicando o motivo da indisponibilidade.
 *
 * Toggle ON → OFF: marca a flag biometric_disabled. Credencial fica
 * intacta no SecureStore.
 *
 * Toggle OFF → ON:
 *  - Se nao ha credencial salva: avisa que precisa logar com senha e
 *    redireciona para a tela de login.
 *  - Se ha credencial: pede biometria para confirmar. Autenticou →
 *    remove a flag. Cancelou/falhou → mantem OFF (forca remount do
 *    toggle via key).
 */

type Props = {
  opacity?: boolean;
};

const { opacity = false } = defineProps<Props>();
const emit = defineEmits(['check-pendency']);

type UnavailableReason =
  | null
  | 'not_native'
  | 'no_hardware'
  | 'not_enrolled_on_device';

const { t } = useTexts();
const { openToaster } = useLayoutStore();
const { openLogin } = useAuth();
const { logout } = useAuthStore();
const {
  isAvailableEnvironment,
  checkAvailability,
  unlock,
  markAsDisabled,
  unmarkAsDisabled,
  isDisabledFlagSet,
  clearOptInDismissed,
} = useBiometricAuth();

const isLoading = ref(false);
const isEditing = ref(false);
const maskedLogin = ref<string | null>(null);
const isBiometricEnabled = ref(false);
const hasStoredCredential = ref(false);
const unavailableReason = ref<UnavailableReason>(null);
// Forca remount do BaseFormToggle quando o usuario cancela: o
// defineModel interno do toggle mantem estado otimista que precisa
// ser resetado.
const toggleKey = ref(0);

const unavailableMessage = computed(() => {
  switch (unavailableReason.value) {
    case 'not_native':
      return t('account.security.biometric.unavailable_not_native');

    case 'no_hardware':
      return t('account.security.biometric.unavailable_no_hardware');

    case 'not_enrolled_on_device':
      return t('account.security.biometric.unavailable_not_enrolled');

    default:
      return '';
  }
});

const isToggleDisabled = computed(
  () => isLoading.value || unavailableReason.value !== null,
);

async function refresh() {
  if (!isAvailableEnvironment()) {
    unavailableReason.value = 'not_native';
    isBiometricEnabled.value = false;
    hasStoredCredential.value = false;

    return;
  }

  const result = await checkAvailability();

  if (!result.hasHardware) {
    unavailableReason.value = 'no_hardware';
  } else if (!result.isEnrolled) {
    unavailableReason.value = 'not_enrolled_on_device';
  } else {
    unavailableReason.value = null;
  }

  hasStoredCredential.value = result.hasCredential;
  isBiometricEnabled.value =
    unavailableReason.value === null &&
    result.hasCredential &&
    !isDisabledFlagSet();
  maskedLogin.value = result.maskedLogin;
}

async function onToggle(newValue: boolean) {
  if (isLoading.value) return;
  if (unavailableReason.value !== null) {
    // Defesa em profundidade — o disabled do toggle ja impede, mas
    // mantemos isso aqui caso algum evento escape.
    toggleKey.value++;

    return;
  }

  if (newValue === false) {
    markAsDisabled();
    isBiometricEnabled.value = false;
    openToaster({
      message: t('account.security.biometric.unactivated'),
      type: 'success',
    });
    emit('check-pendency');

    return;
  }

  // OFF → ON.
  if (!hasStoredCredential.value) {
    openToaster({
      message: t('account.security.biometric.no_credential_hint'),
      type: 'info',
    });
    isBiometricEnabled.value = false;
    clearOptInDismissed();
    try {
      await logout({
        displayMessage: false,
        redirectToHome: false,
        origin: 'user',
      });
    } catch {}
    openLogin();

    return;
  }

  isLoading.value = true;

  try {
    const result = await unlock();

    if (result.success && result.login && result.password) {
      unmarkAsDisabled();
      isBiometricEnabled.value = true;
      openToaster({
        message: t('account.security.biometric.activated'),
        type: 'success',
      });
    } else {
      isBiometricEnabled.value = false;
      toggleKey.value++;
      if (result.error === 'biometric_changed') {
        openToaster({
          message:
            'Detectamos uma alteração na biometria do aparelho. Faça login e ative a biometria novamente.',
          type: 'warning',
        });
        await refresh();
      } else if (result.error && result.error !== 'cancelled') {
        openToaster({
          message: t('account.user_data.errors.update_error'),
          type: 'error',
        });
      }
    }
  } finally {
    isLoading.value = false;
    emit('check-pendency');
  }
}

const hasPendency = computed(() => unavailableReason.value === null);

defineExpose({
  name: 'biometric',
  isEditing,
  hasPendency,
  toggleEdit: () => {
    isEditing.value = !isEditing.value;
  },
});

onMounted(() => {
  refresh();
});

onActivated(() => {
  refresh();
});
</script>

<template>
  <ClientOnly>
    <div :class="[$style.toggleBox, { [$style.opacity]: opacity }]">
      <BaseLoading
        class="!bg-auth-bg-primary !absolute"
        :icon-loader="true"
        :show="isLoading"
      />
      <div :class="$style.header">
        <div :class="$style.titleWrap">
          <p :class="$style.title">
            {{ $t('account.security.biometric.title') }}
          </p>
          <p
            v-if="isBiometricEnabled && maskedLogin"
            :class="$style.subtitle"
          >
            {{ maskedLogin }}
          </p>
        </div>

        <BaseFormToggle
          :key="toggleKey"
          :disabled="isToggleDisabled"
          :model-value="isBiometricEnabled"
          @update:model-value="onToggle"
        />
      </div>

      <p :class="$style.text">
        {{ $t('account.security.biometric.description') }}
      </p>

      <p
        v-if="unavailableMessage"
        :class="$style.unavailable"
      >
        {{ unavailableMessage }}
      </p>
    </div>
  </ClientOnly>
</template>

<style module lang="scss">
.toggleBox {
  @apply flex flex-col gap-3 p-1 md:p-0 px-1 md:px-2;

  &.opacity {
    @apply opacity-20 pointer-events-none select-none;
  }

  .header {
    @apply flex items-center justify-between gap-3;

    .titleWrap {
      @apply flex flex-col;
    }

    .title {
      @apply text-xs sm:text-sm font-semibold;
    }

    .subtitle {
      @apply text-xs text-texts/60;
    }
  }

  .text {
    @apply text-xs text-texts/80;
  }

  .unavailable {
    @apply text-xs text-texts/60 italic;
  }
}
</style>
